<template>
  <Teleport to="body">
    <div v-if="prompt.open" class="staff-pin-backdrop" @click.self="cancel">
      <section ref="dialog" class="staff-pin" role="dialog" aria-modal="true" aria-labelledby="staff-pin-title" tabindex="-1" @keydown="handleKeydown">
        <div class="staff-pin__heading">
          <div>
            <p class="staff-pin__eyebrow"><i class="fa-solid fa-lock" aria-hidden="true"></i> Acción protegida con PIN</p>
            <h2 id="staff-pin-title">{{ selected ? displayName(selected) : prompt.actionLabel }}</h2>
            <p class="staff-pin__subtitle">
              {{ selected ? `Introduce tu PIN para «${prompt.actionLabel}».` : '¿Quién hace esta acción?' }}
            </p>
          </div>
          <button type="button" class="staff-pin__close" aria-label="Cancelar" :disabled="busy" @click="cancel">×</button>
        </div>

        <!-- Con el teclado a la vista el aviso va sobre los puntos: no puede mover las teclas. -->
        <p v-if="error && !selected" class="staff-pin__error" role="alert">{{ error }}</p>

        <template v-if="!selected">
          <p v-if="loading" class="staff-pin__hint" role="status">Cargando equipo…</p>
          <div v-else class="staff-pin__grid">
            <button
              v-for="user in users"
              :key="user.id"
              type="button"
              class="staff-pin__user"
              :disabled="busy || !canChoose(user)"
              @click="choose(user)"
            >
              <span class="staff-pin__avatar" :class="{ 'staff-pin__avatar--partner': user.is_partner }">{{ initial(user) }}</span>
              <strong>{{ displayName(user) }}</strong>
              <small>{{ userStatus(user) }}</small>
            </button>
          </div>
          <p v-if="!loading && users.some(user => !user.has_pin)" class="staff-pin__hint">
            Quien no tiene PIN no puede confirmar acciones. El partner se lo asigna en Personal.
          </p>
        </template>

        <template v-else>
          <div class="staff-pin__pad">
            <p v-if="error" class="staff-pin__pad-notice staff-pin__pad-notice--error" role="alert">{{ error }}</p>
            <p v-else-if="busy" class="staff-pin__pad-notice" role="status">Comprobando PIN…</p>
            <div class="staff-pin__dots" aria-hidden="true">
              <span v-for="i in 4" :key="i" :class="{ filled: pin.length >= i }"></span>
            </div>
            <span class="visually-hidden" aria-live="polite">{{ pin.length }} de 4 dígitos</span>
            <div class="staff-pin__keypad">
              <button v-for="digit in ['1','2','3','4','5','6','7','8','9']" :key="digit" type="button" :disabled="busy" @click="press(digit)">{{ digit }}</button>
              <button type="button" class="staff-pin__key-muted" :disabled="busy || !pin" aria-label="Borrar todo" @click="pin = ''">C</button>
              <button type="button" :disabled="busy" @click="press('0')">0</button>
              <button type="button" class="staff-pin__key-muted" :disabled="busy || !pin" aria-label="Borrar" @click="pin = pin.slice(0, -1)">⌫</button>
            </div>
          </div>
          <button type="button" class="staff-pin__back" :disabled="busy" @click="back">← Elegir otra persona</button>
        </template>
      </section>
    </div>

    <transition name="staff-pin-notice">
      <div v-if="notice.visible" class="staff-pin-notice" role="status">
        <i class="fa-solid fa-user-check" aria-hidden="true"></i>
        <span>Acción a nombre de <strong>{{ notice.name }}</strong></span>
      </div>
    </transition>
  </Teleport>
</template>

<script setup>
import { nextTick, ref, watch } from 'vue';
import {
  completeStaffPin, fetchPinActors, staffActionError, staffActorNotice, staffPinPrompt, verifyStaffPin,
} from '@/services/staff/staffActionService';

const prompt = staffPinPrompt;
const notice = staffActorNotice;
const dialog = ref(null);
const users = ref([]);
const loading = ref(false);
const busy = ref(false);
const error = ref('');
const selected = ref(null);
const pin = ref('');
let previousFocus = null;

const displayName = (user) => user?.name || '';
const initial = (user) => (displayName(user) || '?').charAt(0).toUpperCase();
const canChoose = (user) => Boolean(user.has_pin && user.allowed);

function userStatus(user) {
  if (!user.has_pin) return 'Sin PIN';
  if (!user.allowed) return 'Sin permiso';
  return user.is_partner ? 'Partner' : 'Empleado';
}

async function loadUsers() {
  loading.value = true;
  try {
    users.value = await fetchPinActors(prompt.action);
  } catch (caught) {
    users.value = [];
    error.value = staffActionError(caught, 'No se pudo cargar el equipo. Inténtalo de nuevo.');
  } finally {
    loading.value = false;
  }
}

async function focusDialog() {
  await nextTick();
  dialog.value?.focus();
}

function reset() {
  users.value = [];
  selected.value = null;
  pin.value = '';
  busy.value = false;
}

function choose(user) {
  error.value = '';
  selected.value = user;
  pin.value = '';
  focusDialog();
}

function back() {
  error.value = '';
  selected.value = null;
  pin.value = '';
}

function cancel() {
  if (busy.value) return;
  completeStaffPin(null);
}

async function submit(value) {
  busy.value = true;
  error.value = '';
  try {
    const result = await verifyStaffPin({
      userId: selected.value.id, pin: value, action: prompt.action,
    });
    completeStaffPin(result);
  } catch (caught) {
    const status = Number(caught?.response?.status);
    error.value = staffActionError(caught, 'No se pudo comprobar el PIN.');
    pin.value = '';
    // Sin permiso o sin PIN: que lo haga otra persona.
    if ([400, 403, 404].includes(status)) selected.value = null;
  } finally {
    busy.value = false;
  }
}

function press(digit) {
  if (busy.value || pin.value.length >= 4) return;
  error.value = '';
  pin.value += digit;
  if (pin.value.length === 4) submit(pin.value);
}

function handleKeydown(event) {
  if (event.key === 'Escape') { event.preventDefault(); cancel(); return; }
  if (!selected.value || busy.value) return;
  if (/^\d$/.test(event.key)) { event.preventDefault(); press(event.key); }
  else if (event.key === 'Backspace') { event.preventDefault(); pin.value = pin.value.slice(0, -1); }
}

watch(() => prompt.open, (open) => {
  if (open) {
    previousFocus = document.activeElement;
    reset();
    error.value = prompt.error || '';
    loadUsers();
    focusDialog();
  } else {
    reset();
    error.value = '';
    previousFocus?.focus?.();
    previousFocus = null;
  }
});
</script>

<style scoped>
.staff-pin-backdrop { position: fixed; inset: 0; z-index: 13000; background: rgba(15, 23, 42, .5); display: flex; align-items: center; justify-content: center; padding: 16px; }
.staff-pin { width: 100%; max-width: 560px; max-height: 92vh; overflow: auto; background: #fff; border-radius: 22px; padding: 26px; box-shadow: 0 24px 80px #0003; color: #172033; outline: none; }
.staff-pin__heading { display: flex; justify-content: space-between; gap: 16px; align-items: flex-start; margin-bottom: 14px; }
.staff-pin h2 { font-size: 23px; margin: 0; font-weight: 700; }
.staff-pin__eyebrow { font-size: 12px; text-transform: uppercase; letter-spacing: .07em; color: #F47700; font-weight: 800; margin: 0 0 6px; }
.staff-pin__subtitle { margin: 6px 0 0; color: #64748b; font-size: 14px; }
.staff-pin__close { border: 0; background: #f1f5f9; border-radius: 50%; width: 36px; height: 36px; font-size: 24px; line-height: 1; flex-shrink: 0; }
.staff-pin__error { background: #fff1f2; color: #b42335; border-radius: 10px; padding: 12px; font-size: 14px; }
.staff-pin__hint { color: #64748b; font-size: 13px; line-height: 1.5; margin: 14px 0 0; }
.staff-pin__grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(118px, 1fr)); gap: 12px; }
.staff-pin__user { display: flex; flex-direction: column; align-items: center; gap: 6px; padding: 16px 8px; border: 1px solid #dce3ec; background: #fff; border-radius: 16px; color: #172033; min-width: 0; transition: border-color .15s, background .15s; }
.staff-pin__user strong { max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 14px; }
.staff-pin__user small { color: #64748b; font-size: 12px; }
.staff-pin__user:hover:not(:disabled) { border-color: #F47700; background: #fff8f1; }
.staff-pin__user:disabled { opacity: .55; }
.staff-pin__avatar { width: 52px; height: 52px; border-radius: 50%; display: grid; place-items: center; background: #e9f0fa; font-size: 22px; font-weight: 700; color: #334155; }
.staff-pin__avatar--partner { background: #fff1e5; color: #c25a00; }
.staff-pin__pad { position: relative; display: flex; flex-direction: column; align-items: center; gap: 20px; padding: 6px 0; }
/* Aviso flotante sobre la fila de puntos (el PIN ya se ha vaciado): no ocupa sitio en el flujo. */
.staff-pin__pad-notice { position: absolute; top: -14px; left: 50%; transform: translateX(-50%); z-index: 1; width: max-content; max-width: 100%; margin: 0; padding: 8px 14px; border-radius: 10px; background: #f1f5f9; color: #334155; font-size: 14px; font-weight: 600; line-height: 1.35; text-align: center; box-shadow: 0 6px 18px rgba(15, 23, 42, .12); pointer-events: none; }
.staff-pin__pad-notice--error { background: #fff1f2; color: #b42335; }
.staff-pin__dots { display: flex; gap: 16px; }
.staff-pin__dots span { width: 16px; height: 16px; border-radius: 50%; border: 2px solid #94a3b8; }
.staff-pin__dots span.filled { background: #172033; border-color: #172033; }
.staff-pin__keypad { display: grid; grid-template-columns: repeat(3, 76px); gap: 12px; }
.staff-pin__keypad button { height: 64px; border-radius: 16px; border: 1px solid #dce3ec; background: #fff; font-size: 24px; font-weight: 600; color: #172033; }
.staff-pin__keypad button:active:not(:disabled) { background: #f1f5f9; }
.staff-pin__keypad .staff-pin__key-muted { font-size: 18px; color: #64748b; background: #f8fafc; }
.staff-pin__back { display: block; margin: 18px auto 0; border: 0; background: none; color: #475569; font-size: 14px; font-weight: 600; }
.staff-pin-notice { position: fixed; left: 50%; bottom: 24px; transform: translateX(-50%); z-index: 13001; display: flex; align-items: center; gap: 10px; padding: 12px 18px; border-radius: 999px; background: #0f172a; color: #fff; font-size: 14px; box-shadow: 0 12px 30px rgba(15, 23, 42, .25); }
.staff-pin-notice i { color: #fdba74; }
.staff-pin-notice-enter-active, .staff-pin-notice-leave-active { transition: opacity .2s ease, transform .2s ease; }
.staff-pin-notice-enter-from, .staff-pin-notice-leave-to { opacity: 0; transform: translate(-50%, 8px); }
button:focus-visible { outline: 3px solid #6297ed; outline-offset: 3px; }
.visually-hidden { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
@media (max-width: 540px) {
  .staff-pin { padding: 20px; }
  .staff-pin h2 { font-size: 20px; }
  .staff-pin__keypad { grid-template-columns: repeat(3, 68px); }
}
</style>
